import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EventCombinationSetting, EventFilterEntry, EventFilterInfo, RecordingEvents, RecordingMetadata } from 'common/widgets/recording-player/types';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { REPLAY_EVENT, sessionRecordingQueryUtils } from 'common/widgets/recording-player/query-utils';
import { getSessionRecordingBlockingInfo } from 'common/report/util/feature-sampling';
import { useTranslation } from 'common/i18n/hooks';
import { useLocalStorageManager } from 'common/contexts/react/storage-manager-context';
import { DateRange } from 'common/widgets/date-range-picker/models/date-range';
import { DateRangeType, TimeUnit } from 'common/widgets/date-range-picker/models/types';
import { EVENTS, ResourceType } from 'common/report/constants';
import { FiltersOperator } from 'common/query-builder/query-entry-section/types';
import { SessionReplayHomeHeader } from '../header';
import { SessionReplayMain } from '../main';
import styles from './index.module.styl';
type FetchReplayOptions = {
  append: boolean;
  sentinel?: RecordingMetadata;
};
export function SessionReplayPage() {
  const currentProject = useCurrentProject();
  const i18n = useTranslation();
  const localStorageManager = useLocalStorageManager();
  const [replays, setReplays] = useState<RecordingMetadata[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMoreReplays, setIsLoadingMoreReplays] = useState<boolean>(false);
  const [hasMoreReplays, setHasMoreReplays] = useState<boolean>(true);
  const [currentDateRange, setCurrentDateRange] = useState<DateRange>(new DateRange({
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Day,
      value: 30
    }
  }));
  const [eventFilters, setEventFilters] = useState<EventFilterInfo[]>([{
    name: REPLAY_EVENT,
    filters: []
  }]);
  const [initialEvents, setInitialEvents] = useState<RecordingEvents[]>([]);
  const [replayCount, setReplayCount] = useState<number | null>(null);
  const [eventCombineSetting, setEventCombineSetting] = useState<EventCombinationSetting>(EventCombinationSetting.OR);
  const DEFAULT_FILTER: EventFilterEntry = {
    type: ResourceType.Event,
    event: {
      value: REPLAY_EVENT,
      label: EVENTS.$mp_session_record
    },
    filters: [],
    filtersOperator: FiltersOperator.And
  };
  const [filterEntries, setFilterEntries] = useState<EventFilterEntry[]>([DEFAULT_FILTER]);
  const sentinelRef = useRef<RecordingMetadata | undefined>(undefined);
  const fetchReplaysAndEvents = useCallback(async (_ref: {
    dateRange: DateRange;
    eventInfo: EventFilterInfo[];
    combineSetting: EventCombinationSetting;
    options?: FetchReplayOptions;
  }) => {
    let {
      dateRange,
      eventInfo,
      combineSetting,
      options
    }: {
      dateRange: DateRange;
      eventInfo: EventFilterInfo[];
      combineSetting: EventCombinationSetting;
      options?: FetchReplayOptions;
    } = _ref;
    const currProject = currentProject.get().value;
    if (!currProject) {
      return;
    }
    const {
      recordings: fetchedReplays,
      segmentationLimitReached: isMoreReplays,
      targetEventMapping: targetEvents
    } = await sessionRecordingQueryUtils.fetchRecordingsAndEventsForEventName({
      blockedReplayFrames: getSessionRecordingBlockingInfo(currProject),
      dateRange,
      i18n,
      eventInfo,
      combinationSetting: combineSetting,
      currentProject: currProject,
      sentinelEvent: options === null || options === void 0 ? void 0 : options.sentinel
    });
    setHasMoreReplays(!!isMoreReplays);
    setInitialEvents(targetEvents);
    if (options !== null && options !== void 0 && options.append) {
      setReplays(prev => [...prev, ...fetchedReplays]);
    } else {
      setReplays(fetchedReplays);
      const recordingCount = await sessionRecordingQueryUtils.fetchRecordingsAndEventsForEventNameCount(getSessionRecordingBlockingInfo(currProject), currProject, dateRange, eventInfo);
      setReplayCount(recordingCount);
    }
    if (fetchedReplays.length > 0 && isMoreReplays) {
      const sortedRecordings = [...fetchedReplays].sort((a, b) => a.time - b.time);
      sentinelRef.current = sortedRecordings[0];
    }
    setIsLoading(false);
    setIsLoadingMoreReplays(false);
  }, []);
  const handleLoadMoreReplays = useCallback(() => {
    if (!hasMoreReplays || isLoadingMoreReplays) return;
    setIsLoadingMoreReplays(true);
    fetchReplaysAndEvents({
      dateRange: currentDateRange,
      eventInfo: eventFilters,
      combineSetting: eventCombineSetting,
      options: {
        append: true,
        sentinel: sentinelRef.current
      }
    });
  }, [fetchReplaysAndEvents, currentDateRange, eventFilters, hasMoreReplays, eventCombineSetting, isLoadingMoreReplays]);
  useEffect(() => {
    setIsLoading(true);
    fetchReplaysAndEvents({
      dateRange: currentDateRange,
      eventInfo: eventFilters,
      combineSetting: eventCombineSetting,
      options: {
        append: false
      }
    });
  }, [fetchReplaysAndEvents, currentDateRange, eventFilters, eventCombineSetting]);
  return <div className={styles.page} data-sentry-component="SessionReplayPage" data-sentry-source-file="index.tsx">
      <SessionReplayHomeHeader data-sentry-element="SessionReplayHomeHeader" data-sentry-source-file="index.tsx" />
      <SessionReplayMain replays={replays} replayCount={replayCount} isLoading={isLoading} localStorageManager={localStorageManager} setDateRangeFn={setCurrentDateRange} setEventFilterInfoFn={setEventFilters} dateRange={currentDateRange} loadMoreReplaysFn={handleLoadMoreReplays} isLoadingMoreReplays={isLoadingMoreReplays} initialEvents={initialEvents} setCombinationFn={setEventCombineSetting} combination={eventCombineSetting} filterEntries={filterEntries} setFilterEntries={setFilterEntries} data-sentry-element="SessionReplayMain" data-sentry-source-file="index.tsx" />
    </div>;
}